import React from "react";
import { Box, Button, Typography } from "@mui/material";

/**
 * Shared card for the richer Client Config settings (analytics warehouses,
 * dashboard, lost sales events): a header with title, one-line description
 * and one action, then the body. Keeps those sections visually consistent.
 */
export const settingsText = {
  title: {
    fontFamily: "poppins",
    fontSize: 15,
    fontWeight: 600,
    color: "var(--text-primary)",
    lineHeight: 1.4,
  },
  description: {
    fontFamily: "poppins",
    fontSize: 12.5,
    color: "var(--text-secondary)",
    lineHeight: 1.5,
    mt: 0.25,
  },
  label: {
    fontFamily: "poppins",
    fontSize: 11.5,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--text-secondary)",
  },
  value: {
    fontFamily: "poppins",
    fontSize: 13.5,
    color: "var(--text-primary)",
  },
  muted: {
    fontFamily: "poppins",
    fontSize: 12.5,
    color: "var(--text-secondary)",
  },
};

export const settingsButtonSx = {
  textTransform: "none",
  fontFamily: "poppins",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "8px",
  px: 1.75,
};

/** Small outlined header action ("Edit", "Add event"). */
export const SectionAction = ({ children, ...props }) => (
  <Button
    variant="outlined"
    size="small"
    {...props}
    sx={{
      ...settingsButtonSx,
      borderColor: "var(--border-color)",
      color: "var(--text-primary)",
      "&:hover": { borderColor: "var(--text-secondary)" },
      ...(props.sx || {}),
    }}
  >
    {children}
  </Button>
);

/** Right-aligned Cancel / Save row for an edit form. */
export const SectionFooter = ({ left, children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mt: 2.5,
      pt: 2,
      borderTop: "1px solid var(--border-color)",
    }}
  >
    <Box sx={{ flexGrow: 1 }}>{left}</Box>
    {children}
  </Box>
);

const SettingsSection = ({ title, description, action, children, testId }) => (
  <Box
    component="section"
    aria-label={title}
    data-testid={testId}
    sx={{
      textAlign: "left",
      borderRadius: 2,
      backgroundColor: "var(--bg-paper)",
      border: "1px solid var(--border-color)",
      width: "100%",
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        px: 3,
        pt: 2.5,
        pb: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h3" sx={settingsText.title}>
          {title}
        </Typography>
        {description && (
          <Typography sx={settingsText.description}>{description}</Typography>
        )}
      </Box>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Box>
    <Box sx={{ borderTop: "1px solid var(--border-color)", px: 3, py: 2.5 }}>
      {children}
    </Box>
  </Box>
);

export default SettingsSection;
